import { useRouter } from 'next/router';
import { useAchievementTypes, useReduxState } from 'hooks';
import {
  filterValuesSeparator,
  filterValuesSeparatorSplitRegex
} from 'shared-helpers/consts';
import {
  AchievementRenderAddedBy,
  AchievementUserStatus,
  AchievementCompletionItemsRender
} from 'components/achievements';
import { Badge, Button, FlexContainer, Tooltip } from 'components/common';
import { BoxTableCell } from 'components/common/box';

import type { PageState } from 'types';

interface Props {
  returnPart: 'header' | 'body';
  user?: PageState['user'];
}

const UsersViewAchievement = ({ returnPart, user }: Props) => {
  const {
    messages,
    pageState: { usersAchievementData }
  } = useReduxState();

  const router = useRouter();

  const achievementData =
    usersAchievementData?.[
      (router?.query?.view as string)
        ?.split(filterValuesSeparatorSplitRegex)
        ?.slice(1)
        .join(filterValuesSeparator)
    ]?.[user?.id];

  const { isGroupAchievement, icon } = useAchievementTypes({
    type: achievementData?.[0]?.type
  });

  const handleGroupAchivementRedirect: React.MouseEventHandler<
    HTMLButtonElement
  > = (ev) => {
    ev.stopPropagation();
    router.push(
      `/users/${user.id}/achievements/${achievementData[0]?.AchievementId}`
    );
  };

  const hasMultipleCompletions = achievementData?.length > 1;

  const hasMultipleAddedBySources = achievementData?.some(
    (achievement) =>
      JSON.stringify(achievement.addedBy) !==
      JSON.stringify(achievementData[0]?.addedBy)
  );

  const flexStyles = {
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: 10,
    padding: '5px 0'
  } as React.CSSProperties;

  return returnPart === 'header' ? (
    <>
      <BoxTableCell>{messages.commonAddedBy}</BoxTableCell>
      <BoxTableCell>{messages.commonCompleted}</BoxTableCell>
      <BoxTableCell shrink={true}></BoxTableCell>
    </>
  ) : returnPart === 'body' && user ? (
    <>
      <BoxTableCell label={messages.commonAddedBy}>
        {hasMultipleAddedBySources ? (
          <Tooltip
            content={
              <FlexContainer style={flexStyles}>
                {achievementData.map((achievement, i) => (
                  <AchievementRenderAddedBy
                    key={`${user?.id}-${i}`}
                    addedBy={achievement.addedBy}
                    enableLink={true}
                  />
                ))}
              </FlexContainer>
            }
            isLight={true}
          >
            <Badge isRounded={false} text={messages.commonMultipleSources} />
          </Tooltip>
        ) : (
          <AchievementRenderAddedBy
            addedBy={achievementData?.[0]?.addedBy}
            enableLink={true}
          />
        )}
      </BoxTableCell>

      <BoxTableCell label={messages.commonCompleted}>
        {hasMultipleCompletions ? (
          <Tooltip
            content={
              <FlexContainer style={flexStyles}>
                {achievementData.map((achievement, i) => (
                  <AchievementUserStatus
                    key={`${user?.id}-${i}`}
                    achievementData={achievement}
                    achievementType={achievement.type}
                    numberOfItems={achievement.numberOfTemplateItems}
                  />
                ))}
              </FlexContainer>
            }
            isLight={true}
          >
            <AchievementUserStatus
              achievementData={achievementData[0]}
              achievementType={achievementData[0]?.type}
              numberOfCompletions={achievementData.length}
              numberOfItems={achievementData[0]?.numberOfTemplateItems}
            />
          </Tooltip>
        ) : (
          <AchievementUserStatus
            achievementData={achievementData?.[0]}
            achievementType={achievementData?.[0]?.type}
            numberOfCompletions={achievementData?.length}
            numberOfItems={achievementData?.[0]?.numberOfTemplateItems}
          />
        )}
      </BoxTableCell>

      <BoxTableCell shrink={true}>
        <div
          style={{
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end'
          }}
        >
          {isGroupAchievement ? (
            <Button
              category="transparent"
              icon={icon}
              iconStyle={{ fontSize: '1.4rem', padding: 0 }}
              onClick={handleGroupAchivementRedirect}
              size="small"
            />
          ) : achievementData ? (
            <AchievementCompletionItemsRender
              authorized={achievementData?.[0]?.canSeeCompletionItems}
              cleared={achievementData?.[0]?.completionItemsClearedDate}
              items={achievementData?.[0]?.template}
              renderInModal={true}
              renderInModalProps={{
                category: 'transparent',
                iconStyle: { fontSize: '1.4rem' },
                text: null
              }}
              targetUserId={user.id}
              type={achievementData?.[0]?.type}
            />
          ) : null}
        </div>
      </BoxTableCell>
    </>
  ) : null;
};

export default UsersViewAchievement;
